import { FC, ReactElement } from 'react'
import { Navigate, Route, Routes, useLocation } from 'react-router'

import {
    ConnectedLayout,
    ConnectedNotifications,
    ConnectedNotificationTapRouting,
    ConnectedPushRegistration,
    ConnectedToastsContainer,
} from '@/connectors'
import {
    MY_EXCHANGES,
    MY_SHIFTS,
    OFFERED_EXCHANGES,
    OPEN_SHIFTS,
} from '@/constants'
import { ChatConnectionProvider } from '@/context/ChatConnectionContext'
import { FeedConnectionProvider } from '@/context/FeedConnectionContext'
import { NativeBridgeProvider } from '@/context/NativeBridgeContext'
import { StreamCredentialsProvider } from '@/context/StreamCredentialsContext'
import { trans } from '@/helpers/trans'
import {
    AbsenceDetailPage,
    AbsenceEditPage,
    AbsencesPage,
    ChangePasswordPincodePage,
    CreatePlanningPreferencePage,
    DocumentsPage,
    ErrorPage,
    ExchangeDetailPage,
    ExchangeShiftPage,
    ExchangesPage,
    LanguagePage,
    LeaveOfAbsencesPage,
    LeaveRequestPage,
    LoketHoursPerPeriodPage,
    LoketLeaveBalancesPage,
    MessagesPage,
    NewsfeedPage,
    NotificationSettingsPage,
    PlanningPreferenceDetailPage,
    PlanningPreferencesPage,
    ProfileDetailPage,
    ProfilePage,
    SchedulePage,
    ShiftExchangeDetailPage,
    ShiftRegisterPage,
    ShiftsDetailPage,
    ShiftsPage,
    TracksDetailPage,
    TracksPage,
} from '@/pages'
import deLanguage from '@/translation/de.json'
import enLanguage from '@/translation/en.json'
import nlLanguage from '@/translation/nl.json'

interface RedirectWithLocationProps {
    to: string
}

// Keeps the query string and state of the current location, unlike a plain Navigate
const RedirectWithLocation: FC<RedirectWithLocationProps> = ({ to }): ReactElement => {
    const { search, state } = useLocation()

    return <Navigate to={{ pathname: to, search }} state={state} replace />
}

interface PrivateRouteGroupProps {
    isAuthenticated: boolean
}

export const PrivateRouteGroup: FC<PrivateRouteGroupProps> = ({ isAuthenticated }): ReactElement => {
    const shiftsPath = `/${trans('nav.shifts.link')}`
    const exchangeShiftPath = `/${trans('nav.shifts.exchange.link')}`
    const myShiftsPath = `/${trans('nav.shifts.link')}/${trans('nav.shifts.myShifts.link')}`
    const openShiftsPath = `/${trans('nav.shifts.link')}/${trans('nav.shifts.openShifts.link')}`
    const schedulePath = `/${trans('nav.shifts.link')}/${trans('nav.shifts.schedule.link')}`
    const myExchangesPath = `/${trans('nav.exchanges.link')}/${trans('nav.exchanges.myExchanges.link')}`
    const offeredExchangesPath = `/${trans('nav.exchanges.link')}/${trans('nav.exchanges.offeredExchanges.link')}`
    const absencesPath = `/${trans('nav.absences.link')}/${trans('nav.absences.absences.link')}`
    const leaveOfAbsenceOverviewPath = `/${trans('nav.absences.link')}/${trans('nav.absences.leaveOfAbsenceOverview.link')}`
    const loketHoursPerPeriodPath = `/${trans('nav.absences.link')}/${trans('nav.absences.loketHoursPerPeriod.link')}`
    const leaveOfAbsenceRequestsPath = `/${trans('nav.absences.link')}/${trans('nav.absences.leaveOfAbsenceRequests.link')}`
    const createAbsencesPath = `/${trans('nav.absences.link')}/${trans('nav.absences.createAbsences.link')}`
    const planningPrefPath = `/${trans('nav.planningPreferences.link')}`
    const planningPrefDetailPath = `${planningPrefPath}/:planningPreferenceId`
    const createPlanningPrefPath = `${planningPrefPath}/${trans('nav.planningPreferences.createPlanningPreference.link')}`
    const tracksPath = `/${trans('nav.tracks.link')}`
    const profilePath = `/${trans('nav.profile.link')}`
    const changePassPath = `${profilePath}/${trans('nav.changePasswordPincode.link')}`
    const notificationSettingsPath = `${profilePath}/${trans('nav.notificationSettings.link')}`
    const profileDetailPath = `${profilePath}/:detailId`
    const languagePath = `/${trans('nav.language.link')}`
    const communicationPath = `/${trans('nav.communication.link')}`
    const newsfeedPath = `${communicationPath}/${trans('nav.communication.newsfeed.link')}`
    const messagesPath = `${communicationPath}/${trans('nav.communication.messages.link')}`
    // Links from outside the app (e.g. the manager app) can't know the user's language, so the newsfeed path of every
    // language redirects to the one of the current language
    const otherLanguageNewsfeedPaths = [nlLanguage, enLanguage, deLanguage]
        .map(({ nav }) => `/${nav.communication.link}/${nav.communication.newsfeed.link}`)
        .filter((path, index, paths) => path !== newsfeedPath && paths.indexOf(path) === index)

    return (
        <>
            <ConnectedLayout>
                {isAuthenticated ? (
                    <NativeBridgeProvider>
                        <StreamCredentialsProvider>
                            <FeedConnectionProvider>
                                <ChatConnectionProvider>
                                    <ConnectedNotifications />
                                    <ConnectedPushRegistration />
                                    <ConnectedNotificationTapRouting />
                                    <Routes>

                                        { /* Tracks */ }
                                        <Route path={tracksPath} element={<TracksPage />} />
                                        <Route
                                            path="/"
                                            element={<Navigate to={tracksPath} replace />}
                                        />
                                        <Route path={`/${trans('nav.tracks.link')}/:trackUuid`} element={<TracksDetailPage />} />

                                        { /* Shifts */ }
                                        <Route
                                            path={myShiftsPath}
                                            element={<ShiftsPage tab={MY_SHIFTS} />}
                                        />
                                        <Route
                                            path={openShiftsPath}
                                            element={<ShiftsPage tab={OPEN_SHIFTS} />}
                                        />
                                        <Route
                                            path={shiftsPath}
                                            element={<Navigate to={myShiftsPath} replace />}
                                        />
                                        <Route
                                            path={schedulePath}
                                            element={<SchedulePage />}
                                        />
                                        <Route path={`${shiftsPath}/:shiftUuid`} element={<ShiftsDetailPage />} />
                                        <Route path={`${shiftsPath}/:shiftUuid${exchangeShiftPath}`} element={<ExchangeShiftPage />} />

                                        <Route
                                            path={`${shiftsPath}/:shiftUuid/${trans('nav.shifts.register.link')}`}
                                            element={<ShiftRegisterPage />}
                                        />

                                        { /* Exchanges */ }
                                        <Route
                                            path={myExchangesPath}
                                            element={<ExchangesPage tab={MY_EXCHANGES} />}
                                        />
                                        <Route
                                            path={offeredExchangesPath}
                                            element={<ExchangesPage tab={OFFERED_EXCHANGES} />}
                                        />
                                        <Route
                                            path={`/${trans('nav.exchanges.link')}`}
                                            element={<Navigate to={myExchangesPath} replace />}
                                        />
                                        <Route
                                            path={`/${trans('nav.exchanges.link')}/:exchangeUuid`}
                                            element={<ExchangeDetailPage />}
                                        />
                                        <Route
                                            path={`/${trans('nav.exchanges.shiftExchange.link')}/:exchangeUuid`}
                                            element={<ShiftExchangeDetailPage />}
                                        />

                                        { /* Absences */ }
                                        <Route
                                            path={leaveOfAbsenceOverviewPath}
                                            element={<LoketLeaveBalancesPage />}
                                        />
                                        <Route
                                            path={loketHoursPerPeriodPath}
                                            element={<LoketHoursPerPeriodPage />}
                                        />
                                        <Route
                                            path={leaveOfAbsenceRequestsPath}
                                            element={<LeaveOfAbsencesPage />}
                                        />
                                        <Route
                                            path={absencesPath}
                                            element={<AbsencesPage />}
                                        />
                                        <Route
                                            path={`/${trans('nav.absences.link')}`}
                                            element={<Navigate to={leaveOfAbsenceRequestsPath} replace />}
                                        />
                                        <Route
                                            path={createAbsencesPath}
                                            element={<LeaveRequestPage />}
                                        />

                                        <Route
                                            path={`/${trans('nav.absences.link')}/:id`}
                                            element={<AbsenceDetailPage />}
                                        />

                                        <Route
                                            path={`/${trans('nav.absences.link')}/:id/${trans('common.edit')}`}
                                            element={<AbsenceEditPage />}
                                        />

                                        { /* Documents */ }
                                        <Route path={`/${trans('nav.documents.link')}`} element={<DocumentsPage />} />

                                        { /* Password & Pincode */ }
                                        <Route
                                            path={changePassPath}
                                            element={<ChangePasswordPincodePage />}
                                        />

                                        { /* Planning preference create */ }
                                        <Route
                                            path={createPlanningPrefPath}
                                            element={<CreatePlanningPreferencePage />}
                                        />

                                        { /* Planning preference */ }
                                        <Route
                                            path={planningPrefDetailPath}
                                            element={<PlanningPreferenceDetailPage />}
                                        />

                                        <Route
                                            path={planningPrefPath}
                                            element={<PlanningPreferencesPage />}
                                        />

                                        { /* Profile */ }
                                        <Route
                                            path={notificationSettingsPath}
                                            element={<NotificationSettingsPage />}
                                        />

                                        <Route
                                            path={`${profileDetailPath}/*`}
                                            element={<ProfileDetailPage />}
                                        />

                                        <Route
                                            path={profilePath}
                                            element={<ProfilePage />}
                                        />

                                        { /* Language */ }
                                        <Route
                                            path={`${languagePath}/*`}
                                            element={<LanguagePage />}
                                        />

                                        { /* Valk Talk (communication) */ }
                                        <Route
                                            path={communicationPath}
                                            element={<Navigate to={newsfeedPath} replace />}
                                        />
                                        <Route path={newsfeedPath} element={<NewsfeedPage />} />
                                        {otherLanguageNewsfeedPaths.map(path => (
                                            <Route
                                                key={path}
                                                path={path}
                                                element={<RedirectWithLocation to={newsfeedPath} />}
                                            />
                                        ))}
                                        <Route path={`${messagesPath}/*`} element={<MessagesPage />} />

                                        { /* Error */ }
                                        <Route path="*" element={<ErrorPage errorCode={404} />} />
                                    </Routes>
                                </ChatConnectionProvider>
                            </FeedConnectionProvider>
                        </StreamCredentialsProvider>
                    </NativeBridgeProvider>
                ) : (
                    <Navigate to={`/${trans('nav.login.link')}`} replace />
                )}
            </ConnectedLayout>
            <ConnectedToastsContainer />
        </>
    )
}
